import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  AnimationDirection,
  ANIMATION_STAGE,
  CarouselContentAnimateStage,
} from '../utils';

type UseContentAnimationProps = {
  children: React.ReactNode;
  direction: AnimationDirection;
  withOpacity?: boolean;
};

export default function useContentAnimation({
  children,
  direction,
  withOpacity,
}: UseContentAnimationProps) {
  const elementRef = useRef<HTMLElement>();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [transitionStage, setTransitionStage] =
    useState<CarouselContentAnimateStage>(ANIMATION_STAGE.FADE_IN);

  const animatedClasses = classNames(
    'carousel-content-animation',
    `to-${direction}`,
    { translation: direction !== 'none', opacity: withOpacity },
    transitionStage,
  );

  const onTransitionEnd: React.TransitionEventHandler<HTMLElement> =
    useCallback(
      ({ nativeEvent: { propertyName, target } }) => {
        if (!elementRef.current) elementRef.current = target as HTMLElement;

        if (propertyName === 'transform') {
          if (transitionStage === ANIMATION_STAGE.PRE_FADE_IN) {
            setTransitionStage(ANIMATION_STAGE.FADE_IN);
            setDisplayChildren(children);

            return;
          }

          if (transitionStage === ANIMATION_STAGE.FADE_OUT) {
            setTransitionStage(ANIMATION_STAGE.PRE_FADE_IN);

            return;
          }
        }

        if (withOpacity && propertyName === 'opacity' && direction === 'none') {
          setDisplayChildren(children);
          setTransitionStage(ANIMATION_STAGE.FADE_IN);
          return;
        }
      },
      [children, direction, transitionStage, withOpacity],
    );

  useEffect(() => {
    let opacity = null;
    const { current: el } = elementRef;
    if (el) {
      opacity = +window.getComputedStyle(elementRef.current).opacity;
    }
    if (
      children !== displayChildren &&
      (withOpacity || direction !== 'none') &&
      (!el || !!opacity)
    ) {
      setTransitionStage(ANIMATION_STAGE.FADE_OUT);
    }
  }, [children, direction, displayChildren, withOpacity]);

  return { displayChildren, animatedClasses, onTransitionEnd };
}
